import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import SEO from "../components/SEO";
import { ArrowLeft, ArrowUpRight, Share2, Check } from "lucide-react";

export default function PortfolioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/portfolio/${id}`)
      .then((r) => setItem(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!item) return;
    api.get("/portfolio")
      .then((r) => {
        const list = (r.data || [])
          .filter((p) => p.id !== item.id && p.category === item.category)
          .slice(0, 3);
        setRelated(list);
      })
      .catch(() => setRelated([]));
  }, [item]);

  if (loading) {
    return <div data-testid="portfolio-detail-loading" className="max-w-5xl mx-auto px-6 lg:px-12 py-20 text-[#525252]">Loading…</div>;
  }
  if (notFound || !item) {
    return (
      <div data-testid="portfolio-detail-notfound" className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-2">404</div>
        <h1 className="font-display text-4xl font-bold">Portfolio item not found</h1>
        <p className="mt-4 text-[#525252]">It may have been removed or unapproved.</p>
        <Link to="/portfolio" data-testid="portfolio-detail-back-404" className="mt-6 inline-flex items-center gap-2 text-[var(--brand-primary)] hover:underline">
          <ArrowLeft size={16}/> Back to Portfolio
        </Link>
      </div>
    );
  }

  const before = item.before_image_id ? fileUrl(item.before_image_id) : null;
  const after = item.after_image_id ? fileUrl(item.after_image_id) : null;
  const gallery = Array.isArray(item.gallery) ? item.gallery : [];

  // Pick the most representative image for social previews: restaurant logo > after > before.
  const ogImageId = item.restaurant_logo_image_id || item.after_image_id || item.before_image_id;
  const ogImage = ogImageId ? fileUrl(ogImageId) : null;
  const ogTitle = item.restaurant_name
    ? (item.title?.toLowerCase().includes(item.restaurant_name.toLowerCase())
        ? `${item.title} — Portfolio`
        : `${item.restaurant_name} — ${item.title}`)
    : `${item.title} — Portfolio`;

  return (
    <div data-testid="portfolio-detail-page">
      <SEO
        title={ogTitle}
        description={(item.description || "").slice(0, 200) || "Restaurant branding case study by RestroSetup"}
        path={`/portfolio/${item.slug || item.id}`}
        image={ogImage}
      />

      <section className="border-b border-black/5 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-start justify-between gap-4 mb-6">
            <button onClick={() => navigate(-1)} data-testid="portfolio-detail-back"
                    className="inline-flex items-center gap-1 text-sm text-[#525252] hover:text-[var(--brand-primary)]">
              <ArrowLeft size={14}/> Back
            </button>
            <ShareButton item={item} />
          </div>
          <div className="text-sm uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-3">{item.category}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-tight font-black">
            {item.title}
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {(before || after) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-testid="portfolio-detail-images">
              {before && (
                <figure className="relative border border-black/10 overflow-hidden bg-[#FAFAFA]">
                  <figcaption className="absolute top-3 left-3 z-10 bg-white/90 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">Before</figcaption>
                  <img src={before} alt={`${item.title} before`} className="w-full h-auto block" />
                </figure>
              )}
              {after && (
                <figure className="relative border border-black/10 overflow-hidden bg-[#FAFAFA]">
                  <figcaption className="absolute top-3 left-3 z-10 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">After</figcaption>
                  <img src={after} alt={`${item.title} after`} className="w-full h-auto block" />
                </figure>
              )}
            </div>
          )}

          {item.description && (
            <div className="prose prose-neutral max-w-none mb-12" data-testid="portfolio-detail-description">
              <h2 className="font-display text-2xl font-bold mb-4">About this project</h2>
              <p className="text-[#0A0A0A] leading-relaxed whitespace-pre-line">{item.description}</p>
            </div>
          )}

          {gallery.length > 0 && (
            <div data-testid="portfolio-detail-gallery" className="mb-12">
              <h2 className="font-display text-2xl font-bold mb-6">Project gallery</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {gallery.map((g, idx) => (
                  <figure key={`${g.image_id}-${idx}`} data-testid={`gallery-figure-${idx}`}
                          className="border border-black/10 overflow-hidden bg-[#FAFAFA]">
                    <img
                      src={fileUrl(g.image_id)}
                      alt={g.title || `${item.title} gallery image ${idx + 1}`}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                    {g.title && (
                      <figcaption className="px-4 py-3 text-sm text-[#0A0A0A] border-t border-black/5">
                        {g.title}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-black/5 pt-8 flex flex-wrap items-center gap-4">
            <Link to="/portfolio" data-testid="portfolio-detail-back-list"
                  className="inline-flex items-center gap-1 text-sm text-[#525252] hover:text-[var(--brand-primary)]">
              <ArrowLeft size={14}/> All portfolio
            </Link>
            <Link to="/contact" data-testid="portfolio-detail-cta"
                  className="ml-auto inline-flex items-center gap-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium">
              Get the same for your restaurant <ArrowUpRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#FAFAFA] border-t border-black/5" data-testid="portfolio-related">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#525252] mb-2">More work</div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold">More from <span className="text-[var(--brand-primary)]">{item.category}</span></h2>
              </div>
              <Link to="/portfolio" className="text-sm font-medium inline-flex items-center gap-1 hover:text-[var(--brand-primary)]" data-testid="related-view-all">
                View all <ArrowUpRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => {
                const cover = p.restaurant_logo_image_id || p.after_image_id || p.before_image_id;
                return (
                  <Link
                    key={p.id}
                    to={`/portfolio/${p.slug || p.id}`}
                    data-testid={`related-item-${p.id}`}
                    className="block bg-white border border-black/5 overflow-hidden group hover:border-[var(--brand-primary)]/30 transition-colors"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#F0F0F0]">
                      {cover ? (
                        <img src={fileUrl(cover)} alt={`${p.title} cover`} loading="lazy"
                             className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      ) : null}
                      <div className="absolute top-3 left-3 z-10 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
                        {p.category}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-semibold leading-snug">{p.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] group-hover:gap-2 transition-all">
                        View more <ArrowUpRight size={14}/>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}


function ShareButton({ item }) {
  const [copied, setCopied] = useState(false);
  const slug = item.slug || item.id;
  // Use the user-facing domain's /api/og/portfolio/<slug> route.
  // - Emergent ingress proxies /api/* to the backend automatically
  // - Backend returns crawler-friendly HTML with portfolio-specific OG tags
  // - Real browsers get an instant client-side redirect to /portfolio/<slug>
  // This means WhatsApp/FB/Twitter render the restaurant's image AND show
  // the friendly "restrosetup.com" hostname (not the backend hostname).
  const shareUrl = `${window.location.origin}/api/og/portfolio/${slug}`;
  const shareTitle = item.restaurant_name
    ? `${item.restaurant_name} — RestroSetup case study`
    : `${item.title} — RestroSetup`;
  const shareText = (item.description || "").slice(0, 160);

  const onShare = async () => {
    // Prefer the OS-native share sheet on mobile.
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the URL bar instead");
    }
  };

  return (
    <button
      type="button"
      onClick={onShare}
      data-testid="portfolio-share-button"
      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-black/10 rounded-sm hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors"
    >
      {copied
        ? <><Check size={14}/> Copied</>
        : <><Share2 size={14}/> Share</>}
    </button>
  );
}
